import React from 'react';
import {
	Box,
	Card,
	CardContent,
	Container,
	Typography,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Button,
	Alert,
	IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import RefreshIcon from '@mui/icons-material/Refresh';
import StoreIcon from '@mui/icons-material/Store';
import LogoutIcon from '@mui/icons-material/Logout';
import {
	useGetPlaceNamesMutation,
	useLoginToPlaceMutation,
	useInvalidateMutation,
} from '../../store/api/api';
import { useHistoryNav } from '../../hooks/useHistoryNav';
import torioLogo from '../../assets/torio_logo.png';
import { onLogout } from '../../api/helper';
import { useDispatch } from 'react-redux';
import { resetLoginData } from '../../store/login/loginSlice';

const SelectPlace = ({ modalState, showBackButton }: any) => {
	const [loginToPlace, loginStatus] = useLoginToPlaceMutation();
	const [getPlaceNames, result] = useGetPlaceNamesMutation();
	const [selectedPlace, setSelectedPlace] = React.useState('');
	const [invalidate] = useInvalidateMutation();
	const [isRefreshing, setIsRefreshing] = React.useState(false);
	const history = useHistoryNav();
	const dispatch = useDispatch();

	let places =
		result && result?.data?.items && result?.data?.items.length > 0
			? result?.data?.items
			: [];

	React.useEffect(() => {
		getPlaceNamesFun(false);
		window.scrollTo({ top: 0 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getPlaceNamesFun = async (shouldGet) => {
		setIsRefreshing(true);
		await getPlaceNames(shouldGet);
		setIsRefreshing(false);
	};

	const handleLogout = async () => {
		await onLogout(() => {}, invalidate);
		dispatch(resetLoginData());
		setTimeout(() => {
			window.location.reload();
		}, 500);
	};

	return (
		<Box
			sx={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
				padding: 2,
				position: 'relative',
			}}
		>
			{/* Logout Button */}
			<IconButton
				onClick={handleLogout}
				sx={{
					position: 'absolute',
					top: { xs: 8, sm: 16 },
					right: { xs: 8, sm: 16 },
					bgcolor: 'rgba(255, 255, 255, 0.2)',
					color: 'white',
					'&:hover': {
						bgcolor: 'rgba(255, 255, 255, 0.3)',
					},
				}}
			>
				<LogoutIcon />
			</IconButton>

			<Container maxWidth="sm">
				<Card
					elevation={6}
					sx={{
						borderRadius: 3,
						overflow: 'visible',
					}}
				>
					<CardContent sx={{ p: { xs: 3, sm: 5 } }}>
						{/* Logo */}
						<Box sx={{ textAlign: 'center', mb: 4 }}>
							<img
								src={torioLogo}
								alt="Torio Logo"
								style={{
									width: 120,
									height: 'auto',
									marginBottom: 16,
								}}
							/>
							<Typography
								variant="h4"
								component="h1"
								fontWeight={700}
								gutterBottom
								sx={{ color: 'grey.900' }}
							>
								Select place
							</Typography>
							<Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
								Select the place you want to log into
							</Typography>
						</Box>

						{/* Place Selection */}
						{places && places.length > 0 ? (
							<>
								<FormControl fullWidth sx={{ mb: 2 }}>
									<InputLabel id="place-select-label">Place</InputLabel>
									<Select
										labelId="place-select-label"
										id="place-select"
										value={selectedPlace}
										label="Place"
										onChange={(e) => setSelectedPlace(e.target.value)}
										startAdornment={
											<StoreIcon sx={{ mr: 1, color: 'action.active' }} />
										}
									>
										<MenuItem value="">
											<em>Select a place to log into</em>
										</MenuItem>
										{places.map((option, index) => (
											<MenuItem key={index} value={option._id}>
												{option.name}{' '}
												{option?.detailedPlace?.isActive ? '(On Barhopp)' : ''}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								{/* Refresh Button */}
								<Button
									fullWidth
									variant="text"
									startIcon={<RefreshIcon />}
									onClick={() => getPlaceNamesFun(true)}
									disabled={isRefreshing}
									sx={{
										mb: 3,
										textTransform: 'none',
										color: 'primary.main',
									}}
								>
									{isRefreshing ? 'Refreshing...' : 'Refresh list'}
								</Button>

								{/* Continue Button */}
								<LoadingButton
									fullWidth
									variant="contained"
									size="large"
									loading={loginStatus.isLoading}
									disabled={!selectedPlace}
									onClick={async () => {
										let loginToPlaceObj = await loginToPlace({
											placeId: selectedPlace,
											modal: modalState,
										});

										//@ts-ignore
										let pushUrl = loginToPlaceObj?.data?.pushUrl;

										//@ts-ignore
										if (loginToPlaceObj?.data?.success) {
											//@ts-ignore
											let newUrl = window.location.origin + '/' + pushUrl;
											//@ts-ignore
											window.location.href = newUrl;
										}
									}}
									sx={{
										py: 1.5,
										fontSize: '1rem',
										fontWeight: 600,
										textTransform: 'none',
										borderRadius: 2,
										mb: showBackButton ? 2 : 0,
									}}
								>
									Continue
								</LoadingButton>

								{/* Back Button */}
								{showBackButton && (
									<Button
										fullWidth
										variant="outlined"
										size="large"
										onClick={() => history(-1)}
										sx={{
											py: 1.5,
											fontSize: '1rem',
											fontWeight: 600,
											textTransform: 'none',
											borderRadius: 2,
										}}
									>
										Back
									</Button>
								)}
							</>
						) : (
							<Alert severity="info" sx={{ textAlign: 'center' }}>
								You do not have access to any places. You need an invite link to
								manage a place.
							</Alert>
						)}
					</CardContent>
				</Card>
			</Container>
		</Box>
	);
};

export default SelectPlace;
